import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { CheckCircle2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cancelCrashByToken } from '@/api/endpoints/crashes';
import { ApiError } from '@/api/client';

/**
 * The target of the one-time link in the crash-alert SMS. No login — the token in
 * the URL is the credential. A click is required (a link preview must not cancel a
 * real emergency), and the token is single-use and only valid while the crash is
 * still pending.
 */
export function CancelCrashPage() {
  const { token = '' } = useParams<{ token: string }>();

  const cancel = useMutation({
    mutationFn: () => cancelCrashByToken(token),
  });

  const tooLate = cancel.error instanceof ApiError && cancel.error.status === 409;

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm rounded-[--radius-card] border border-line bg-surface p-6 text-center shadow-sm">
        {cancel.isSuccess ? (
          <>
            <CheckCircle2 className="mx-auto size-10 text-ok" aria-hidden />
            <h1 className="mt-3 text-lg font-semibold text-ink">Alert cancelled</h1>
            <p className="mt-1 text-sm text-ink-secondary">
              We&apos;ve stopped the alert. No one else will be contacted. Stay safe.
            </p>
          </>
        ) : tooLate ? (
          <>
            <ShieldAlert className="mx-auto size-10 text-bad" aria-hidden />
            <h1 className="mt-3 text-lg font-semibold text-ink">Too late to cancel</h1>
            <p className="mt-1 text-sm text-ink-secondary">
              The cancel window has closed and help is already being contacted. If
              you&apos;re OK, let them know directly.
            </p>
          </>
        ) : (
          <>
            <ShieldAlert className="mx-auto size-10 text-bad" aria-hidden />
            <h1 className="mt-3 text-lg font-semibold text-ink">Are you OK?</h1>
            <p className="mt-1 text-sm text-ink-secondary">
              AutoAware detected an impact on your vehicle. If you don&apos;t need
              help, cancel the alert now.
            </p>
            <Button
              variant="danger"
              className="mt-4 w-full"
              onClick={() => cancel.mutate()}
              loading={cancel.isPending}
            >
              I&apos;m OK — cancel the alert
            </Button>
            {cancel.isError && !tooLate && (
              <p className="mt-2 text-[13px] text-bad">
                That link is no longer valid.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
